import { useCallback, useEffect, useState } from 'react';
import BrandLogo from './components/BrandLogo';
import { api, type User } from './lib/api';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';

function wantsAuthGate(): boolean {
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get('ticket') || params.get('host') === '1');
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);
  const [authGate, setAuthGate] = useState(wantsAuthGate);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me.user);
      setBootError(null);
      setAuthGate(false);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        setUser(null);
      } else {
        setBootError('Unable to reach Zemew Theatre. Please refresh.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="app-shell" style={{ placeItems: 'center', display: 'grid' }}>
        <div className="brand fade-up">
          <BrandLogo />
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="app-shell" style={{ placeItems: 'center', display: 'grid' }}>
        <div className="error-banner">{bootError}</div>
      </div>
    );
  }

  if (!user && authGate) {
    return (
      <LoginPage
        onLogin={(u) => {
          setUser(u);
          setAuthGate(false);
        }}
        onWatchInstead={() => {
          const url = new URL(window.location.href);
          url.searchParams.delete('ticket');
          url.searchParams.delete('host');
          window.history.replaceState({}, '', url.pathname + url.search);
          setAuthGate(false);
        }}
      />
    );
  }

  if (!user) {
    return (
      <Dashboard
        user={null}
        watchOnly
        onSignIn={() => setAuthGate(true)}
      />
    );
  }

  return (
    <Dashboard
      user={user}
      onLogout={async () => {
        await api.logout();
        setUser(null);
        setAuthGate(false);
      }}
    />
  );
}
